import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUiV2 } from '../../shell/uiFlags';

type StatusType = 'active' | 'idle' | 'error' | 'warning' | 'live' | 'disabled';

interface StatusDotProps {
  status: StatusType;
  size?: number;
  showLabel?: boolean;
  label?: string;
}

const statusColorConfig: Record<StatusType, { color: string; pulse: boolean; defaultLabel: string; key: string }> = {
  live: { color: '#059669', defaultLabel: 'En vivo', key: 'common.statusLive', pulse: true },
  active: { color: '#059669', defaultLabel: 'Activo', key: 'common.statusActive', pulse: false },
  idle: { color: '#94A3B8', defaultLabel: 'Inactivo', key: 'common.statusIdle', pulse: false },
  error: { color: '#DC2626', defaultLabel: 'Error', key: 'common.statusError', pulse: false },
  warning: { color: '#D97706', defaultLabel: 'Advertencia', key: 'common.statusWarning', pulse: false },
  // Operator-set, unlike every other value here, which the system writes.
  // Deliberately NOT grey like `idle`: idle means nothing has happened yet,
  // disabled means somebody turned it off. Reading one as the other is how
  // a switched-off number gets mistaken for a quiet one.
  disabled: { color: '#64748B', defaultLabel: 'Deshabilitado', key: 'common.statusDisabled', pulse: false },
};

// New interface (ui_v2): the dot takes the theme's state tokens (Blueprint
// intents in v2, lighter in dark) and the label reads as plain text.
const statusTokenV2: Record<StatusType, string> = {
  live: 'var(--color-status-green)',
  active: 'var(--color-status-green)',
  idle: 'var(--color-text-subtle)',
  error: 'var(--color-status-red)',
  warning: 'var(--color-warn)',
  disabled: 'var(--color-text-muted)',
};

export const StatusDot: React.FC<StatusDotProps> = ({
  status,
  size = 8,
  showLabel = false,
  label,
}) => {
  const { t } = useTranslation();
  const uiV2 = useUiV2();
  const config = statusColorConfig[status] || statusColorConfig.idle;
  const dotColor = uiV2 ? (statusTokenV2[status] || statusTokenV2.idle) : config.color;
  const displayLabel = label || t(config.key, config.defaultLabel);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        className={config.pulse ? 'status-dot-live' : undefined}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: dotColor,
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
      {showLabel && (
        <span style={{
          fontSize: '12px',
          color: uiV2 ? 'var(--color-text)' : config.color,
          fontWeight: uiV2 ? 400 : 500,
        }}>
          {displayLabel}
        </span>
      )}
    </span>
  );
};

export default StatusDot;

